/** @format */
import { useMachine } from '@xstate/react';
import React, {
    Dispatch,
    createContext,
    useContext,
    useMemo,
    useReducer,
} from 'react';

import {
    CheckoutAction,
    CheckoutActionType,
    CheckoutState,
    checkoutReducer,
} from '@toucan/context/checkout-reducer';

import {
    CheckoutMachineEntryActions,
    getCheckoutMachine,
    isCheckoutSectionSubmitting,
} from '@toucan/client/core/checkout';
import {
    BasketTransactionSummary,
    CheckoutTransactionSummary,
    TransactionSummary,
} from '@toucan/client/global/transaction';

export const CheckoutContext = createContext<CheckoutContextType | undefined>(
    undefined
);

type CheckoutContextType = {
    state: CheckoutState;
    dispatch: Dispatch<CheckoutAction>;
    isSubmitting: boolean;
    current: ReturnType<typeof useMachine>[0];
    send: ReturnType<typeof useMachine>[1];
    transaction?:
        | TransactionSummary
        | BasketTransactionSummary
        | CheckoutTransactionSummary;
};

export const CheckoutProvider: React.FC<{
    children: React.ReactNode;
    initialState: CheckoutState;
}> = ({ initialState, children }) => {
    const [state, dispatch] = useReducer(checkoutReducer, initialState);
    const checkoutMachine = useMemo(
        () =>
            getCheckoutMachine({
                actions: {
                    [CheckoutMachineEntryActions.CLEAR_BALANCE_PAYMENT]: () => {
                        dispatch({
                            type: CheckoutActionType.SET_USE_BALANCE,
                            payload: false,
                        });
                        dispatch({
                            type: CheckoutActionType.SET_BALANCE_AMOUNT,
                            payload: 0,
                        });
                    },
                },
            }),
        []
    );
    const [current, send] = useMachine(checkoutMachine);
    const isSectionSubmitting = isCheckoutSectionSubmitting(current);
    const isSubmitting = state.isSubmitting || isSectionSubmitting;

    const transaction =
        state.createdTransactionInfo && state.transaction
            ? { ...state.transaction, ...state.createdTransactionInfo }
            : state.transaction;

    return (
        <CheckoutContext.Provider
            value={{
                state,
                dispatch,
                isSubmitting,
                current,
                send,
                transaction,
            }}
        >
            {children}
        </CheckoutContext.Provider>
    );
};

export const useCheckout = () => {
    const context = useContext(CheckoutContext);
    if (!context) {
        throw new Error('useCheckout must be used within a CheckoutProvider');
    }
    return context;
};
